import { Col } from "@amzn/stencil-react-components/layout";
import React, { FC } from "react";
import { FLOW_ERROR_ID } from "@/common/components/FlowShell/flow-shell";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { StepError, StepSpinner } from "@/pages/Replacement/components/step-status";
import type { PassedDeviceContext } from "@/pages/Replacement/hooks/use-replaced-device.hook";
import { DeliveryMethodOption } from "./components/delivery-method-option";
import { DeliveryOptionsEmpty } from "./components/delivery-options-empty";
import { PickupPanel } from "./components/pickup-panel";
import { ShipPanel } from "./components/ship-panel";
import { useChooseDeliveryStep } from "./hooks/use-choose-delivery-step.hook";
import { CHOOSE_DELIVERY_STRINGS } from "./choose-delivery.constants";

export interface ChooseDeliveryStepProps {
  assetId: string;
  passedContext?: PassedDeviceContext;
  supportTicketHref: string;
  /** Whether the flow is showing a Continue message, so the method choice can point at it. */
  hasGateError: boolean;
}

export const ChooseDeliveryStep: FC<ChooseDeliveryStepProps> = ({
  assetId,
  passedContext,
  supportTicketHref,
  hasGateError,
}) => {
  const {
    choices,
    deliveryMethod,
    selectedBuilding,
    isLoading,
    isError,
    isEmpty,
    refetch,
    shippingAddress,
    chooseMethod,
    chooseBuilding,
  } = useChooseDeliveryStep({ assetId, passedContext });

  if (isLoading) return <StepSpinner testId="choose-delivery-loading" />;
  if (isError) {
    return (
      <StepError
        testId="choose-delivery-error"
        message={getString(`${CHOOSE_DELIVERY_STRINGS}.optionsLoadError`)}
        onRetry={() => refetch()}
      />
    );
  }
  if (isEmpty) return <DeliveryOptionsEmpty supportTicketHref={supportTicketHref} />;

  const hasMethodError = hasGateError && !deliveryMethod;

  return (
    <Col
      gridGap="S300"
      width="100%"
      role="radiogroup"
      aria-label={getString(`${CHOOSE_DELIVERY_STRINGS}.methodsLabel`)}
      aria-describedby={hasMethodError ? FLOW_ERROR_ID : undefined}
    >
      {choices.pickupBuildings.length > 0 && (
        <DeliveryMethodOption
          method="pickup"
          isSelected={deliveryMethod === "pickup"}
          hasError={hasMethodError}
          onSelect={chooseMethod}
        >
          <PickupPanel
            buildings={choices.pickupBuildings}
            selectedBuilding={selectedBuilding}
            onSelectBuilding={chooseBuilding}
          />
        </DeliveryMethodOption>
      )}
      {choices.isShipAvailable && (
        <DeliveryMethodOption
          method="ship"
          isSelected={deliveryMethod === "ship"}
          hasError={hasMethodError}
          onSelect={chooseMethod}
        >
          <ShipPanel editor={shippingAddress} />
        </DeliveryMethodOption>
      )}
    </Col>
  );
};
